"use client";

import React, { useState, useCallback, useMemo, useRef } from "react";
import { AccordionGroupContext, type AccordionGroupContextValue } from "./AccordionGroupContext";
export type AccordionGroupProps = {
  showExpandAll?: boolean;
  expandAllLabel?: string;
  children: React.ReactNode;
  className?: string;
};
const TOGGLE_TRACK = ["relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer"].join(" ");
const TOGGLE_THUMB = "inline-block h-4 w-4 rounded-full bg-white shadow transition-transform";
export const AccordionGroup = ({
  showExpandAll = false,
  expandAllLabel = "Expand All",
  children,
  className = ""
}: AccordionGroupProps) => {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  const [expandAllActive, setExpandAllActive] = useState(false);
  const registeredItems = useRef(new Set<string>());
  const expandAllActiveRef = useRef(false);
  const registerItem = useCallback((id: string) => {
    registeredItems.current.add(id);
    if (expandAllActiveRef.current) {
      setOpenStates(prev => ({
        ...prev,
        [id]: true
      }));
    }
    return () => {
      registeredItems.current.delete(id);
      setOpenStates(prev_0 => {
        const next = {
          ...prev_0
        };
        delete next[id];
        return next;
      });
    };
  }, []);
  const isItemOpen = useCallback((id_0: string) => expandAllActive || !!openStates[id_0], [openStates, expandAllActive]);
  const toggleItem = useCallback((id_1: string) => {
    if (expandAllActiveRef.current) {
      expandAllActiveRef.current = false;
      setExpandAllActive(false);
      const next_0: Record<string, boolean> = {};
      for (const itemId of registeredItems.current) {
        next_0[itemId] = itemId !== id_1;
      }
      setOpenStates(next_0);
      return;
    }
    setOpenStates(prev_1 => ({
      ...prev_1,
      [id_1]: !prev_1[id_1]
    }));
  }, []);
  const allExpanded = useMemo(() => {
    if (expandAllActive) return true;
    // eslint-disable-next-line react-hooks/refs
    const items = Array.from(registeredItems.current);
    return items.length > 0 && items.every(id_2 => openStates[id_2]);
  }, [openStates, expandAllActive]);
  const toggleExpandAll = useCallback(() => {
    if (!allExpanded) {
      expandAllActiveRef.current = true;
      setExpandAllActive(true);
      const next_1: Record<string, boolean> = {};
      for (const id_3 of registeredItems.current) {
        next_1[id_3] = true;
      }
      setOpenStates(next_1);
      return;
    }
    expandAllActiveRef.current = false;
    setExpandAllActive(false);
    setOpenStates({});
  }, [allExpanded]);
  const contextValue: AccordionGroupContextValue = useMemo(() => ({
    registerItem,
    isItemOpen,
    toggleItem
  }), [registerItem, isItemOpen, toggleItem]);
  return <AccordionGroupContext.Provider value={contextValue}>
      <div className={className}>
        {showExpandAll && <div className="flex items-center justify-start gap-3 mb-4 px-0">
            <span className="text-sm font-bold text-gray-m9 t2-dark:text-white select-none">
              {expandAllLabel}
            </span>
            <button type="button" role="switch" aria-checked={allExpanded} aria-label={allExpanded ? "Collapse all items" : "Expand all items"} onClick={toggleExpandAll} className={`${TOGGLE_TRACK} ${allExpanded ? "bg-blue-trmb" : "bg-gray-m3"}`}>
              <span className={`${TOGGLE_THUMB} ${allExpanded ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>}
        {children}
      </div>
    </AccordionGroupContext.Provider>;
};